import React from 'react';

import { useDataModule } from '@perpay-web/data-module/useDataModule';
import {
    createDataModuleStateObservable,
    useDataModuleStateObservable,
} from '@perpay-web/data-module/useDataModuleStateObservable';
import { useReduxDataModuleHookConnection } from '@perpay-web/data-module/useReduxDataModuleHookConnection';
import {
    paychexOnboardingSignup,
    paychexOnboardingSignupError,
    paychexOnboardingSignupReset,
    paychexOnboardingSignupSuccess,
} from '@perpay-web/fintech/actions/entities/paychexOnboarding';
import { authentication } from '@perpay-web/fintech/settings/singletons';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

export type PaychexSignUpPayload = {
    firstName: string;
    lastName: string;
    email: string;
    mobilePhone: string;
    password: string;
    paychex_campaign: boolean;
};

type PaychexOnboardingSignupDataModule = {
    state: {
        requestState: string;
        errors: { [key: string]: unknown };
        value: unknown;
    };
    dataRequest: (payload: PaychexSignUpPayload) => void;
    dataReset: VoidFunction;
    sideEffect$: unknown;
    isLoading: boolean;
    isComplete: boolean;
    errors: { [key: string]: string[] } | {};
    data: unknown;
    isError: boolean;
};

// Create the state observable for use in epics
export const paychexOnboardingSignupDataModuleState$ =
    createDataModuleStateObservable();

const usePaychexOnboardingSignupDataModule = () => {
    const dataModule = useDataModule((payload: PaychexSignUpPayload) =>
        authentication.paychexHostedSignup(payload),
    );

    // Connect the data module state to the observable for use in epics
    useDataModuleStateObservable(
        dataModule,
        paychexOnboardingSignupDataModuleState$,
    );

    useReduxDataModuleHookConnection({
        dataModule,
        hookAction$,
        request: paychexOnboardingSignup,
        success: paychexOnboardingSignupSuccess,
        error: paychexOnboardingSignupError,
        reset: paychexOnboardingSignupReset,
    });

    return dataModule;
};

const providerFactory = (
    Context: React.Context<PaychexOnboardingSignupDataModule>,
) => {
    const Provider = ({ children }: { children: React.ReactNode }) => {
        const dataModule = usePaychexOnboardingSignupDataModule();
        return (
            <Context.Provider value={dataModule}>{children}</Context.Provider>
        );
    };
    return Provider;
};

export const [
    UsePaychexOnboardingSignupDataModuleProvider,
    usePaychexOnboardingSignupContext,
] = createContextProvider(providerFactory);
